import { useContext, useEffect, useState } from 'react';

import { AlertTriangle } from '@gympass/yoga-icons';
import withAuth from 'hocs/withAuth/withAuth';
import { FormattedMessage, useIntl } from 'react-intl';

import { SpecialistContext } from 'context/SpecialistContext';
import useOnInit from 'hooks/useOnInit';
import {
  getSpecialistDashboardCritical,
  getSpecialistDashboardUnlink,
} from 'services/specialist/specialist';
import {
  SpecialistDashboardCritical,
  SpecialistDashboardUnlink,
  SpecialistRole,
} from 'services/specialist/types';

import { CPDashboardTable, CPDashboardHeader, Loading } from 'components';
import { defaultTabsConfig } from 'components/CPDashboardHeader/configs';
import { TabData, TabName, TabState } from 'components/CPDashboardTab/types';
import CPDashboardNoCriticals from 'images/cp-dashboard-no-criticals.svg';

import { CPDashboardTableData } from './types';

import * as S from './styles';

const DEFAULT_INITIAL_TAB = TabName.CRITICAL;

const CareProviderDashboard: React.FC = () => {
  const intl = useIntl();

  const { specialistInfo } = useContext(SpecialistContext);

  const [currentTabName, setCurrentTabName] =
    useState<TabName>(DEFAULT_INITIAL_TAB);
  const [tabsData, setTabsData] = useState<TabData[]>(defaultTabsConfig);
  const [tableLoading, setTableLoading] = useState(true);
  const [tables, setTables] = useState<CPDashboardTableData[]>([]);
  const [showWillUnlinkBanner, setShowWillUnlinkBanner] = useState<boolean>(
    currentTabName === TabName.WILL_BE_UNLINKED,
  );
  const [dashboardCriticalData, setDashboardCriticalData] =
    useState<SpecialistDashboardCritical>({
      data: [],
      metadata: { total_users: 0, total_critical_users: 0 },
    });
  const [dashboardUnlinkData, setDashboardUnlinkData] =
    useState<SpecialistDashboardUnlink>({
      Data: [],
      Metadata: { total_unlinks: 0 },
    });

  const currentTable = tables.find(({ name }) => name === currentTabName);

  const handleChangeTabName = (tabName: TabName) => {
    setCurrentTabName(tabName);
    setShowWillUnlinkBanner(tabName === TabName.WILL_BE_UNLINKED);
  };

  const loadSpecialistDashboardData = async () => {
    setTableLoading(true);

    const dashboardCritical = await getSpecialistDashboardCritical();
    setDashboardCriticalData(dashboardCritical);

    const dashboardUnlink = await getSpecialistDashboardUnlink();
    setDashboardUnlinkData(dashboardUnlink);

    const updatedTabsData = tabsData.map(({ name }) => {
      let tabOverviewCount;

      if (name === TabName.CRITICAL) {
        tabOverviewCount = dashboardCritical.metadata.total_critical_users;
      }

      if (name === TabName.WILL_BE_UNLINKED) {
        tabOverviewCount = dashboardUnlink.Metadata.total_unlinks;
      }

      const getState = () => {
        return currentTabName === name ? TabState.ACTIVE : TabState.INACTIVE;
      };

      return {
        name,
        state: getState(),
        amount: tabOverviewCount || 0,
      };
    });

    setTabsData(updatedTabsData);

    setTableLoading(false);
  };

  const mountSpecialistDashboardTable = (): void => {
    if (
      currentTabName === TabName.CRITICAL &&
      dashboardCriticalData.metadata.total_critical_users
    ) {
      const decodedData = dashboardCriticalData.data.map(data => {
        return {
          cognitoId: data.cognito_id,
          fullName: data.full_name,
          criticality: data.criticality,
          nextSession: data.next_session,
          classificationDate: data.classification_date,
          status: data.is_confirmed,
          identification: data.origin,
        };
      });

      setTables([...tables, { name: currentTabName, data: decodedData }]);
    }

    if (
      currentTabName === TabName.WILL_BE_UNLINKED &&
      dashboardUnlinkData.Metadata.total_unlinks
    ) {
      const decodedData = dashboardUnlinkData.Data.map(data => {
        return {
          cognitoId: data.cognito_id,
          fullName: data.full_name,
          reason: data.reason,
          userExpiredAt: data.user_expired_at,
          userId: data.user_id,
        };
      });

      setTables([...tables, { name: currentTabName, data: decodedData }]);
    }
  };

  useOnInit(() => {
    loadSpecialistDashboardData();
  });

  useEffect(() => {
    mountSpecialistDashboardTable();
  }, [
    dashboardCriticalData,
    dashboardUnlinkData,
    currentTable,
    currentTabName,
  ]);

  return (
    <>
      <Loading isLoading={tableLoading} />

      <S.CPDashboardContainer data-testid="cpDashboardPage">
        {!tableLoading && (
          <CPDashboardHeader
            specialistName={specialistInfo?.fullName}
            numberOfPatients={dashboardCriticalData.metadata.total_users}
            tabsData={tabsData}
            handleChangeTab={handleChangeTabName}
          />
        )}
        <S.Section>
          {showWillUnlinkBanner && (
            <S.WillUnlinkBanner
              icon={AlertTriangle}
              margin="small"
              variant="attention"
              message={intl.formatMessage({
                id: 'ELIGIBILITYSHUTDOWNCPTC.WARNINGBANNER.PSYCHOLOGICALRECORD',
              })}
              primaryButton={{
                label: intl.formatMessage({
                  id: 'ELIGIBILITYSHUTDOWNCPTC.BUTTON.CLOSE',
                }),
                action: () => setShowWillUnlinkBanner(false),
              }}
            />
          )}

          {!tableLoading && currentTable?.data ? (
            <CPDashboardTable
              loading={tableLoading}
              filterType={currentTabName}
              patientsInfo={currentTable.data}
            />
          ) : (
            <S.NoResults data-testid="cpDashboardNoCriticalResult">
              <CPDashboardNoCriticals />
              <S.NoResultText>
                <FormattedMessage id="CARE_PROVIDER.DASHBOARD.TABLE.NO_RESULTS" />
              </S.NoResultText>
            </S.NoResults>
          )}
        </S.Section>
      </S.CPDashboardContainer>
    </>
  );
};

export default withAuth(CareProviderDashboard, [SpecialistRole.CP]);
